import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AudioCheckStep,
  INITIAL_AUDIO_CHECK_STEPS,
  beginMicrophoneRequest,
  runRecordingAudioChecks,
} from './recordingAudioCheck';
import './RecordingAudioCheckModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onPassed: () => void;
  beatTitle?: string;
}

const RecordingAudioCheckModal: React.FC<Props> = ({ open, onClose, onPassed, beatTitle }) => {
  const [steps, setSteps] = useState<AudioCheckStep[]>(INITIAL_AUDIO_CHECK_STEPS);
  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');
  const [allPassed, setAllPassed] = useState(false);
  const runIdRef = useRef(0);

  const passedCount = steps.filter(s => s.status === 'passed').length;
  const progress =
    phase === 'done' && allPassed ? 100 : Math.round((passedCount / steps.length) * 100);

  const runChecks = () => {
    // CRITICAL (Chrome/Edge): start getUserMedia in this click's synchronous turn.
    // Do not await or setState before beginMicrophoneRequest().
    let micPromise: Promise<MediaStream>;
    try {
      micPromise = beginMicrophoneRequest();
    } catch (err) {
      ++runIdRef.current;
      setPhase('done');
      setAllPassed(false);
      setSteps(
        INITIAL_AUDIO_CHECK_STEPS.map(s =>
          s.id === 'microphone'
            ? {
                ...s,
                status: 'failed' as const,
                detail:
                  err instanceof Error
                    ? err.message
                    : 'Could not start microphone request. Refresh and try again.',
              }
            : s.id === 'browser'
              ? { ...s, status: 'passed' as const, detail: 'Browser supports recording.' }
              : { ...s, status: 'pending' as const, detail: undefined }
        )
      );
      return;
    }

    const runId = ++runIdRef.current;
    setPhase('running');
    setAllPassed(false);
    setSteps(INITIAL_AUDIO_CHECK_STEPS.map(s => ({ ...s, status: 'pending' as const, detail: undefined })));

    void (async () => {
      const result = await runRecordingAudioChecks(
        nextSteps => {
          if (runIdRef.current === runId) {
            setSteps(nextSteps);
          }
        },
        { micPromise }
      );

      if (runIdRef.current !== runId) {
        // Stale run — release mic if the promise still resolves
        void micPromise
          .then(stream => stream.getTracks().forEach(t => t.stop()))
          .catch(() => {});
        return;
      }

      setSteps(result.steps);
      setAllPassed(result.passed);
      setPhase('done');
    })();
  };

  useEffect(() => {
    if (!open) {
      runIdRef.current += 1;
      setPhase('idle');
      setAllPassed(false);
      setSteps(INITIAL_AUDIO_CHECK_STEPS.map(s => ({ ...s, status: 'pending' as const, detail: undefined })));
      return;
    }

    setPhase('idle');
    setAllPassed(false);
    setSteps(INITIAL_AUDIO_CHECK_STEPS.map(s => ({ ...s, status: 'pending' as const, detail: undefined })));

    return () => {
      runIdRef.current += 1;
    };
  }, [open]);

  if (!open) return null;

  // Portal to body so ancestors with transform/backdrop-filter/overflow cannot clip the overlay.
  return createPortal(
    <div className="rec-audio-check" role="dialog" aria-modal="true" aria-labelledby="rec-audio-check-title">
      <div className="rec-audio-check__panel">
        <h2 id="rec-audio-check-title" className="rec-audio-check__title">
          Audio check before recording
        </h2>
        <p className="rec-audio-check__subtitle">
          {beatTitle
            ? `Quick setup for recording on “${beatTitle}”. Pass once per browser session.`
            : 'Quick setup so your verse records clearly. Pass once per browser session.'}
        </p>
        {phase === 'idle' && (
          <p className="rec-audio-check__hint">
            Tap <strong>Start checks</strong> so Chrome/Edge can open your microphone (must start from
            this click).
          </p>
        )}

        <div className="rec-audio-check__progress-wrap">
          <div className="rec-audio-check__progress-bar">
            <div className="rec-audio-check__progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="rec-audio-check__progress-label">
            {phase === 'idle'
              ? 'Ready to start'
              : phase === 'running'
                ? 'Running checks…'
                : allPassed
                  ? 'All checks passed'
                  : 'Check failed'}
          </span>
        </div>

        <ul className="rec-audio-check__steps">
          {steps.map(step => (
            <li key={step.id} className={`rec-audio-check__step rec-audio-check__step--${step.status}`}>
              <span className="rec-audio-check__step-icon" aria-hidden>
                {step.status === 'passed' && '✓'}
                {step.status === 'failed' && '✕'}
                {step.status === 'running' && <span className="rec-audio-check__spinner" />}
                {(step.status === 'pending' || step.status === 'skipped') && '○'}
              </span>
              <div className="rec-audio-check__step-body">
                <span className="rec-audio-check__step-label">{step.label}</span>
                <span className="rec-audio-check__step-desc">{step.detail || step.description}</span>
              </div>
            </li>
          ))}
        </ul>

        <div className="rec-audio-check__actions">
          {phase === 'idle' ? (
            <button type="button" className="rec-audio-check__btn rec-audio-check__btn--primary" onClick={runChecks}>
              Start checks
            </button>
          ) : phase === 'done' && allPassed ? (
            <button type="button" className="rec-audio-check__btn rec-audio-check__btn--primary" onClick={onPassed}>
              Continue to recording
            </button>
          ) : phase === 'done' && !allPassed ? (
            <button type="button" className="rec-audio-check__btn rec-audio-check__btn--primary" onClick={runChecks}>
              Retry checks
            </button>
          ) : (
            <button type="button" className="rec-audio-check__btn rec-audio-check__btn--muted" disabled>
              Running…
            </button>
          )}
          <button
            type="button"
            className="rec-audio-check__btn rec-audio-check__btn--ghost"
            onClick={onClose}
            disabled={phase === 'running'}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecordingAudioCheckModal;
